import { useNavigate } from '@tanstack/react-router';
import { useCallback, useRef, useMemo } from 'react';
import { useEffect, useState } from 'react';

import Female1 from '@common/assets/images/verification/female-1.png';
import Female2 from '@common/assets/images/verification/female-2.png';
import Female3 from '@common/assets/images/verification/female-3.png';
import Female4 from '@common/assets/images/verification/female-4.png';
import Male1 from '@common/assets/images/verification/male-1.png';
import Male2 from '@common/assets/images/verification/male-2.png';
import Male3 from '@common/assets/images/verification/male-3.png';
import Male4 from '@common/assets/images/verification/male-4.png';
import { useTelegramBackButton } from '@common/hooks';
import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Icon } from '@common/ui/Icon';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';
import { useEventEmit } from '@common/utils/events';

import {
  ERROR_PHOTO_TITLE,
  UNKNOWN_PHOTO_ERROR_MESSAGE,
  useUploader,
} from '@entities/profile/hooks/useUploader';

import { analytics } from '@services/AnalyticsService';
import { usePostVerify } from '@services/hooks/AuthService';

const images: Record<string, string[]> = {
  male: [Male1, Male2, Male3, Male4],
  female: [Female1, Female2, Female3, Female4],
};

export type VerificationProps = {
  gender: 'male' | 'female';
  canPass?: boolean;
};

export const Verification: React.FC<VerificationProps> = ({
  gender,
  canPass = false,
}) => {
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const navigate = useNavigate();

  const { trigger: triggerPostVerify, isMutating: isMutatingPostVerify } =
    usePostVerify();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useTelegramBackButton({ to: 'back', enabled: !canPass });

  // Track when user starts verification process
  useEffect(() => {
    analytics.trackVerificationStarted('photo');
  }, []);

  const uploader = useUploader({
    onUnknownError: () =>
      emit('error', {
        title: ERROR_PHOTO_TITLE,
        description: UNKNOWN_PHOTO_ERROR_MESSAGE,
      }),
    onFileTooLarge: ({ message }) =>
      emit('error', {
        title: ERROR_PHOTO_TITLE,
        description: message,
      }),
  });

  const openCameraOrGallery = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      setIsLoading(true);
      const file = event.target.files?.[0];

      if (file && event.target.files) {
        const responseUrl = await uploader({
          file: event.target.files[0],
        });
        setPhoto(responseUrl);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetakePhoto = useCallback(() => {
    setPhoto(null);
    openCameraOrGallery();
  }, [openCameraOrGallery]);

  const handleContinue = useCallback(async () => {
    if (photo) {
      try {
        await triggerPostVerify({ photo_url: photo, notes: 'TEST' });

        // Track successful verification submission
        analytics.trackVerificationSubmitted('photo');

        emit('info', {
          title: 'Фото успешно загружено',
          description:
            'Дождитесь проверки фотографии, мы сообщим вам, когда она будет проверена',
        });

        navigate({ to: '/profile' });
      } catch (e) {
        emit('error', {
          title: 'Ошибка верификации',
          description: 'Не удалось пройти верификацию. Попробуйте еще раз.',
        });
      }
    }
  }, [emit, navigate, photo, triggerPostVerify]);

  useEffect(() => {
    tabbar.setBottomSheet(
      <BottomSheet
        buttons={[
          photo ? (
            <>
              <Button
                testId="verification-continue-button"
                onClick={handleContinue}
                loading={isMutatingPostVerify || isLoading}
              >
                Далее
              </Button>
              <Button
                testId="verification-retake-button"
                onClick={handleRetakePhoto}
                variant="actionSecondary"
              >
                Перефотографироваться
              </Button>
            </>
          ) : (
            <>
              <Button
                testId="verification-open-camera-button"
                onClick={openCameraOrGallery}
                loading={isLoading}
              >
                Сфотографироваться
              </Button>
              {canPass && (
                <Button
                  testId="verification-skip-button"
                  onClick={() => navigate({ to: '/dating' })}
                  variant="actionSecondary"
                >
                  Пропустить
                </Button>
              )}
            </>
          ),
        ]}
      />,
    );
  }, [photo, isMutatingPostVerify, isLoading]);

  const image = useMemo(() => {
    return images[gender][Math.floor(Math.random() * images[gender].length)];
  }, [gender]);

  return (
    <>
      <Heading align="center" level={2}>
        Подтверждение личности
      </Heading>
      <Paragraph align="center" variant="secondary" compact>
        Пропускаем к себе только настоящих людей
        <br />
        Нужно сделать селфи, повторив позу и жест как на фотографии. Обязательно
        проверьте освещение
      </Paragraph>

      <Icon
        testId="verification-profile-icon"
        shape="squircle"
        image={photo || image}
        style={{
          width: 176,
          height: 176,
          margin: '0 auto',
        }}
      />

      <input
        data-test-id="verification-file-input"
        type="file"
        accept="capture=camera,image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handlePhotoChange}
      />
    </>
  );
};

Verification.displayName = 'Verification';
